import type { QueryKey, QueryClient, UseMutationOptions } from '@tanstack/react-query';

/**
 * Options for {@link optimisticMutation}, modelling the shared TanStack
 * optimistic-update shape used by the enable/disable/delete mutations in
 * useConnectors / useSkills: cancel in-flight queries for a single key, snapshot
 * the current cache, optimistically patch it, and roll back the snapshot on
 * error.
 */
interface OptimisticMutationOptions<TData, TVariables, TCache> {
  queryClient: QueryClient;
  queryKey: QueryKey;
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Pure cache transform applied optimistically in `onMutate`. */
  updater: (current: TCache | undefined, variables: TVariables) => TCache | undefined;
}

export interface OptimisticContext<TCache> {
  previous: TCache | undefined;
}

/**
 * Build a `useMutation` options object that optimistically patches a single
 * query key and rolls the snapshot back on error. Encapsulates the
 * cancelQueries → getQueryData → setQueryData → onError-restore boilerplate so
 * each mutation only has to declare its key, mutationFn, and the cache
 * transform.
 *
 * Deliberately scoped to the single-key, snapshot-and-rollback pattern. Multi
 * key mutations (e.g. disconnect, which touches two caches) and mutations that
 * also reconcile via `onSuccess` keep their explicit options.
 */
export function optimisticMutation<TData, TVariables, TCache>({
  queryClient,
  queryKey,
  mutationFn,
  updater,
}: OptimisticMutationOptions<TData, TVariables, TCache>): UseMutationOptions<
  TData,
  Error,
  TVariables,
  OptimisticContext<TCache>
> {
  return {
    mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TCache>(queryKey);
      queryClient.setQueryData<TCache | undefined>(queryKey, (old) => updater(old, variables));
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  };
}
